import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  initTelegramSDK,
  isInsideTelegram,
  useBackButton,
  useStartParam,
} from './lib/telegram';
import { HomeScreen } from './screens/HomeScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { SubmitScreen } from './screens/SubmitScreen';
import { AdminScreen } from './screens/AdminScreen';
import { ConfirmScreen } from './screens/ConfirmScreen';
import { RegisterChoiceScreen } from './screens/RegisterChoiceScreen';
import { CollectionScreen } from './screens/CollectionScreen';
import { ReferralsScreen } from './screens/ReferralsScreen';
import { LeaderboardScreen } from './screens/LeaderboardScreen';
import { BottomNav } from './components/BottomNav';
import type { NavTab } from './components/BottomNav';
import { ToastContainer } from './components/Toast';
// v0 reskin applied
import type { Agent } from './types/agent';

// Screen types for routing
type Screen =
  | { type: 'home' }
  | { type: 'agent'; id: string }
  | { type: 'submit' }
  | { type: 'edit'; id: string; agent: Agent }
  | { type: 'admin' }
  | { type: 'confirm'; requestId: string }
  | { type: 'register-choice' }
  | { type: 'collection'; slug: string };

function App() {
  const [screen, setScreen] = useState<Screen>({ type: 'home' });
  const [activeTab, setActiveTab] = useState<NavTab>('home');
  const [initialized, setInitialized] = useState(false);

  // Get startapp param for deep linking
  const startParam = useStartParam();

  // Initialize Telegram SDK on mount
  useEffect(() => {
    const success = initTelegramSDK();
    setInitialized(true);

    if (!success && !isInsideTelegram()) {
      console.log('Running outside Telegram');
    }
  }, []);

  // Parse startapp param for deep links, or hash for /admin
  useEffect(() => {
    if (!initialized) return;

    // Check for /admin route via hash
    if (window.location.hash === '#/admin' || window.location.pathname === '/admin') {
      setScreen({ type: 'admin' });
      return;
    }

    if (startParam) {
      if (startParam === 'admin') {
        setScreen({ type: 'admin' });
      } else if (startParam.startsWith('confirm_')) {
        const requestId = startParam.slice(8);
        if (requestId) setScreen({ type: 'confirm', requestId });
      } else if (startParam.startsWith('ref_')) {
        const referralCode = startParam.slice(4);
        if (referralCode) {
          // Store referral code for later use during registration
          sessionStorage.setItem('teleclaw_referral', referralCode);
        }
        // Navigate to home - referral tracking is the goal
        setScreen({ type: 'home' });
      } else if (startParam.startsWith('agent_')) {
        const agentId = startParam.slice(6);
        if (agentId) {
          setScreen({ type: 'agent', id: agentId });
        }
      }
    }
  }, [startParam, initialized]);

  // Navigation helpers
  const navigateToAgent = useCallback((agentId: string) => {
    setScreen({ type: 'agent', id: agentId });
  }, []);

  const navigateHome = useCallback(() => {
    setScreen({ type: 'home' });
    setActiveTab('home');
  }, []);

  const handleTabChange = useCallback((tab: NavTab) => {
    if (tab === 'submit') {
      setScreen({ type: 'register-choice' });
      return;
    }
    setActiveTab(tab);
    setScreen({ type: 'home' }); // Reset to home-level screens
  }, []);

  const navigateToSubmit = useCallback(() => {
    setScreen({ type: 'register-choice' });
  }, []);

  const navigateToManualSubmit = useCallback(() => {
    setScreen({ type: 'submit' });
  }, []);

  const navigateToEdit = useCallback((agent: Agent) => {
    setScreen({ type: 'edit', id: agent.id, agent });
  }, []);

  const navigateToCollection = useCallback((slug: string) => {
    setScreen({ type: 'collection', slug });
  }, []);

  // BackButton integration
  const isHome = screen.type === 'home' && ['home', 'submit', 'referrals', 'leaderboard'].includes(activeTab);
  const { show: showBackButton, hide: hideBackButton } = useBackButton(
    isHome ? undefined : navigateHome
  );

  // Show/hide back button based on current screen
  useEffect(() => {
    if (isHome) {
      hideBackButton();
    } else {
      showBackButton();
    }
  }, [isHome, showBackButton, hideBackButton]);

  // Check if inside Telegram
  const insideTelegram = useMemo(() => isInsideTelegram(), []);

  // Show prompt if not in Telegram
  if (!insideTelegram && initialized) {
    return (
      <div className="min-h-screen bg-[var(--tg-theme-bg-color)] text-[var(--tg-theme-text-color)] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <p className="text-6xl mb-6">🦞</p>
          <h1 className="text-2xl font-bold mb-3">TeleClaw</h1>
          <p className="text-[var(--tg-theme-hint-color)] mb-6">
            This app is designed to run inside Telegram.
          </p>
          <a
            href="https://t.me/tele_claw_bot/app"
            className="inline-block px-6 py-3 bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] hover:brightness-110 rounded-xl font-medium transition-colors"
          >
            Open in Telegram
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--tg-theme-bg-color)] text-[var(--tg-theme-text-color)]">
      <ToastContainer />

      {/* Removed duplicate "Discover" header — title is inside HomeScreen */}

      {/* Main content */}
      <main className="p-4" style={{ paddingTop: 'calc(var(--safe-top, 0px) + 48px)' }} key={screen.type + activeTab + ('id' in screen ? (screen as any).id : '') + ('slug' in screen ? (screen as any).slug : '')}>
        {screen.type === 'home' && activeTab === 'home' && (
          <HomeScreen onAgentClick={navigateToAgent} onSubmitClick={navigateToSubmit} onCollectionClick={navigateToCollection} />
        )}
        {screen.type === 'home' && activeTab === 'referrals' && (
          <ReferralsScreen onAgentClick={navigateToAgent} />
        )}
        {screen.type === 'home' && activeTab === 'leaderboard' && (
          <LeaderboardScreen onAgentClick={navigateToAgent} />
        )}
        {screen.type === 'agent' && (
          <ProfileScreen agentId={screen.id} onBack={navigateHome} onEdit={navigateToEdit} />
        )}
        {screen.type === 'submit' && (
          <SubmitScreen onBack={navigateHome} onSuccess={navigateToAgent} />
        )}
        {screen.type === 'edit' && (
          <SubmitScreen 
            onBack={navigateHome} 
            onSuccess={navigateToAgent} 
            agentId={screen.id}
            existingAgent={screen.agent}
          />
        )}
        {screen.type === 'admin' && (
          <AdminScreen onBack={navigateHome} />
        )}
        {screen.type === 'confirm' && (
          <ConfirmScreen requestId={screen.requestId} onBack={navigateHome} />
        )}
        {screen.type === 'register-choice' && (
          <RegisterChoiceScreen onManual={navigateToManualSubmit} onBack={navigateHome} />
        )}
        {screen.type === 'collection' && (
          <CollectionScreen slug={screen.slug} onAgentClick={navigateToAgent} onBack={navigateHome} />
        )}
      </main>

      {/* Bottom navigation - show on home-level screens */}
      {screen.type === 'home' && (
        <BottomNav active={activeTab} onChange={handleTabChange} />
      )}
    </div>
  );
}

export default App;
